<template>
  <button
    type="button"
    class="relative whitespace-nowrap inline-flex items-center justify-center p-0.5 mb-2 mr-2 overflow-hidden text-sm font-medium text-gray-900 rounded-lg group bg-gradient-to-br from-purple-600 to-blue-500 group-hover:from-purple-600 group-hover:to-blue-500 hover:text-white  focus:ring-4 focus:outline-none focus:ring-blue-300 "
    @click="showModal"
  >
    <span class="relative px-5 py-2.5 transition-all ease-in duration-75 bg-white  rounded-md group-hover:bg-opacity-0">
      Notify Me
      <Icon name="mdi:bell-ring" class="w-5 h-5 ml-2 -mr-1" />
    </span>
  </button>
  <client-only>
    <HeadlessTransitionRoot as="template" :show="open">
      <HeadlessDialog as="div" class="relative z-10" @close="open = false">
        <HeadlessTransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </HeadlessTransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <HeadlessTransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <HeadlessDialogPanel
                class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6"
              >
                <div>
                  <div
                    class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100"
                  >
                    <Icon name="mdi:bell-ring" class="h-6 w-6 text-blue-600" />
                  </div>
                  <div class="mt-3 sm:mt-5">
                    <HeadlessDialogTitle
                      as="h3"
                      class="text-base font-semibold leading-6 text-gray-900 text-center "
                    >
                      Get
                      Priority Access
                    </HeadlessDialogTitle>
                    <div class="mt-2">
                      <p class="text-base leading-relaxed text-gray-500 mb-4 mx-12 text-center">
                        Sign up to be notified and get priority access when <span class="font-bold">
                          {{ props.product.name
                          }}
                        </span>
                        tickets are available.
                      </p>
                    </div>
                    <div class="">
                      <div class="mx-12">
                        <FormKit type="form" @submit="submitSignupHandler">
                          <FormKitSchema :schema="formSchema" />
                        </FormKit>
                      </div>
                      <!-- ERROR ALERT -->
                      <div v-if="errorMessage" class="rounded-md bg-red-50 p-4">
                        <div class="flex">
                          <div class="flex-shrink-0">
                            <Icon name="mdi:alert-circle" class="h-5 w-5 text-red-400" />
                          </div>
                          <div class="ml-3">
                            <h3 class="text-sm font-medium text-red-800">
                              There was a problem
                              with your submission
                            </h3>
                            <div class="mt-2 text-sm text-red-700">
                              <ul role="list" class="list-disc space-y-1 pl-5">
                                <li>{{ errorMessage }}</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </HeadlessDialogPanel>
            </HeadlessTransitionChild>
          </div>
        </div>
      </HeadlessDialog>
    </HeadlessTransitionRoot>
  </client-only>
</template>
<script setup lang="ts">

// Runtime config
const config = useRuntimeConfig()

// Pass into this component the TicketType product.
const props = defineProps<{
    product?: any,
    tenantId?: string
}>()

const open = ref(false) // Modal open state
const errorMessage = ref('') // Error message returned from the API

// Control functions for the modal
const closeModal = () => {
  console.log('Closing modal')
  open.value = false
}
const showModal = () => {
  console.log('Opening modal')
  open.value = true
}

// Submit subscriber to back-end
const submitSignupHandler = async (formData) => {
  // Clear any previous error
  errorMessage.value = ''
  try {
    // Post the data to the API
    const { data, error } = await useFetch(`${config.public.WETIX_SERVICE_API_BASE_URL}/v1/public/subscribe-to-mailing-list`,
      {
        method: 'POST',
        body: {
          ...formData,
          mailingListId: `sold-out-notify-me_tickettype_${props?.product.id}`, // Set the mailingListId to a unique list for the given ticketType
          subscriberMeta: {
            subscriptionCreatedByClient: 'wetix-shop/CartNotifyMeModal',
            subscriptionCategory: 'sold-out-notify-me', // Indicates the model for arbiatry data passed into the subscriberMeta
            ticketType: {
              // Pass over basic details about the ticketType, so we can use it in email templates.
              id: props?.product?.id,
              name: props?.product?.name,
              sku: props?.product?.sku,
              eventId: props?.product?.eventId,
              eventName: props?.product?.eventName,
              displayCategory: props?.product?.displayCategory,
              tenantId: useRoute().params.tenant
            }
          }
        }
      }) as any
    if (data.value) {
      console.log('Successfully subscribed', data.value)
      // If the request was successful, close the modal
      closeModal()
    }
    // If there is an error, set the error message
    if (error.value) {
      errorMessage.value = error?.value?.data?.message || 'An unknown error occurred'
    }
  } catch (error) {
    console.error(error)
    errorMessage.value = error?.value?.data?.message || 'An unknown error occurred'
  }
}
// Form schema for subscribe form
const formSchema = [

  {
    $formkit: 'text',
    label: 'First Name',
    name: 'firstName',
    // help: 'Enter your first name',
    validation: '',
    classes: {
      outer: 'first-outer'
    }
  },
  {
    $formkit: 'text',
    label: 'Last Name',
    name: 'lastName',
    // help: 'Enter your last name',
    validation: '',
    classes: {
      outer: 'first-outer'
    }
  },
  {
    $formkit: 'text',
    name: 'email',
    label: 'Email',
    help: 'We will email you when tickets are available.',
    validation: 'required|email'
  },
  {
    $formkit: 'text',
    name: 'mobilePhone',
    label: 'Mobile ',
    // validation: 'matches:((\+44(\s\(0\)\s|\s0\s|\s)?)|0)7\d{3}(\s)?\d{6}$/',
    help: 'We will text you when tickets are available. (optional)'

  },
  {
    $cmp: 'FormKit',
    props: {
      name: 'promoConsent',
      type: 'checkbox',
      id: 'promoConsent',
      validation: 'required',
      label: 'I consent to being contacted when tickets are available.'
    }
  }

]

</script>
